<template>
  <div class="node-selector-wrapper">
    <div class="node-selector-inner">
      <div class="text-center mb-3">
        <img src="../assets/images/logo/logo.png" alt="logo" width="70" />
      </div>

      <h4 class="text-center mb-1">Selecionar Nó de Trabalho</h4>
      <p class="text-center text-muted mb-2" style="font-size: 0.85rem">
        Selecione o nó onde deseja trabalhar.<br />
        <small class="text-warning" v-if="!isAdmin">
          Apenas nós finais (sem sub-nós) podem ser selecionados.
        </small>
      </p>

      <!-- carregando -->
      <div v-if="loading" class="text-center py-4">
        <b-spinner variant="primary" />
      </div>

      <!-- sem nós disponíveis -->
      <div v-else-if="tree.length === 0" class="text-center py-4">
        <feather-icon
          icon="AlertCircleIcon"
          size="48"
          class="text-warning mb-2"
        />
        <p class="text-muted mb-2">Nenhum nó disponível para o seu usuário.</p>
        <b-button v-if="isAdmin" variant="primary" block @click="confirm">
          Continuar sem nó selecionado
        </b-button>
        <b-button
          variant="outline-secondary"
          size="sm"
          class="mt-1"
          @click="logout"
        >
          Sair
        </b-button>
      </div>

      <!-- tree view -->
      <div v-else>
        <!-- busca -->
        <b-form-input
          v-model="search"
          placeholder="Buscar nó..."
          size="sm"
          class="mb-2"
        />

        <div class="nodes-tree-panel">
          <node-selector-item
            v-for="node in filteredTree"
            :key="node.id"
            :node="node"
            :depth="0"
            :selected-id="selected ? selected.id : null"
            :can-select="isAdmin"
            :accessible-ids="accessibleIds"
            @select="selectNode"
          />

          <div
            v-if="filteredTree.length === 0"
            class="text-center text-muted py-3"
            style="font-size: 0.85rem"
          >
            Nenhum nó encontrado para "{{ search }}"
          </div>
        </div>

        <!-- nó selecionado -->
        <div v-if="selected" class="selected-info mt-2">
          <feather-icon
            icon="CheckCircleIcon"
            size="14"
            class="text-success mr-50"
          />
          <span>{{ selected.name }}</span>
          <span class="text-muted ml-50" v-if="selected.type"
            >— {{ selected.type.name }}</span
          >
        </div>

        <b-button
          variant="primary"
          block
          class="mt-2"
          :disabled="!selected && !isAdmin"
          @click="confirm"
        >
          {{
            selected
              ? "Entrar em " + selected.name
              : "Continuar sem nó selecionado"
          }}
        </b-button>

        <b-button
          variant="flat-secondary"
          block
          size="sm"
          @click="logout"
          class="mt-1"
        >
          Sair
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BFormInput } from "bootstrap-vue";
import NodeSelectorItem from "./NodeSelectorItem.vue";

export default {
  components: { BButton, BSpinner, BFormInput, NodeSelectorItem },

  computed: {
    isAdmin() {
      const nivel = Number(
        localStorage.getItem("access_nivel") ||
          this.$store.state.user.access_nivel,
      );
      return nivel === 1;
    },

    // Monta árvore — para admins usa allNodes, para não-admins filtra só nós acessíveis + ancestrais
    tree() {
      const source = this.isAdmin ? this.allNodes : this.visibleNodes;
      const map = {};
      const roots = [];

      source.forEach((n) => {
        map[n.id] = { ...n, children: [] };
      });

      source.forEach((n) => {
        if (n.parent_id && map[n.parent_id]) {
          map[n.parent_id].children.push(map[n.id]);
        } else {
          roots.push(map[n.id]);
        }
      });

      return roots;
    },

    // Nós que não-admins podem ver: os acessíveis + todos os ancestrais
    visibleNodes() {
      if (this.isAdmin) return this.allNodes;

      const nodeMap = {};
      this.allNodes.forEach((n) => {
        nodeMap[n.id] = n;
      });

      const visibleIds = new Set(this.accessibleIds);

      // Para cada nó acessível, adiciona todos os ancestrais
      this.accessibleIds.forEach((id) => {
        let node = nodeMap[id];
        while (node && node.parent_id) {
          visibleIds.add(node.parent_id);
          node = nodeMap[node.parent_id];
        }
      });

      return this.allNodes.filter((n) => visibleIds.has(n.id));
    },

    filteredTree() {
      if (!this.search.trim()) return this.tree;
      const q = this.search.toLowerCase();

      const filterTree = (nodes) =>
        nodes
          .map((n) => ({ ...n, children: filterTree(n.children || []) }))
          .filter(
            (n) =>
              n.name.toLowerCase().includes(q) ||
              (n.type && n.type.name.toLowerCase().includes(q)) ||
              n.children.length > 0,
          );

      return filterTree(this.tree);
    },
  },

  data() {
    return {
      allNodes: [],
      // IDs que o usuário não-admin tem acesso direto (node_users)
      accessibleIds: [],
      selected: null,
      loading: true,
      search: "",
    };
  },

  methods: {
    async loadNodes() {
      const api = this.$store.state.api;
      const userId = this.$store.state.user.id;

      try {
        // Carrega todos os nós para montar a árvore completa
        const allRes = await axios.get(
          api + "nodes?paginated=false&with[]=type",
        );
        this.allNodes = allRes.data.data || [];

        // Para não-admins, carrega quais nós o usuário tem acesso
        if (!this.isAdmin) {
          const userRes = await axios.get(
            api + "node_users?paginated=false&user_id=" + userId,
          );
          const data = userRes.data.data || [];
          this.accessibleIds = data
            .filter((nu) => nu.node_id)
            .map((nu) => nu.node_id);
        }

        // Restaura o nó da sessão anterior, se ainda for folha e acessível
        const saved = this.$store.state.selected_node;
        if (saved) {
          // Verifica se ainda existe e é folha
          const match = this.allNodes.find((n) => n.id === saved.id);
          if (match) {
            const inTree = this.findInTree(this.tree, saved.id);
            if (inTree && this.isLeaf(inTree)) {
              const canSel =
                this.isAdmin || this.accessibleIds.includes(inTree.id);
              if (canSel) this.selected = inTree;
            }
          }
        }

        // Auto-seleciona se houver apenas um nó folha acessível
        if (!this.selected) {
          const leaves = this.getAccessibleLeaves(this.tree);
          if (leaves.length === 1) this.selected = leaves[0];
        }
      } catch {
        this.$message(
          "Erro",
          "Não foi possível carregar os nós disponíveis.",
          "error",
        );
      } finally {
        this.loading = false;
      }
    },

    isLeaf(node) {
      return !node.children || node.children.length === 0;
    },

    findInTree(nodes, id) {
      for (const n of nodes) {
        if (n.id === id) return n;
        if (n.children && n.children.length) {
          const found = this.findInTree(n.children, id);
          if (found) return found;
        }
      }
      return null;
    },

    getAccessibleLeaves(nodes) {
      let leaves = [];
      nodes.forEach((n) => {
        if (this.isLeaf(n)) {
          if (this.isAdmin || this.accessibleIds.includes(n.id)) leaves.push(n);
        } else {
          leaves = leaves.concat(this.getAccessibleLeaves(n.children || []));
        }
      });
      return leaves;
    },

    selectNode(node) {
      this.selected = node;
    },

    async confirm() {
      const node = this.selected || null;
      this.$store.commit("setNode", node);

      // Persiste no backend: tenant_id (org) e current_node_id (nó de trabalho)
      if (node) {
        try {
          await axios.post(this.$store.state.api + "select-node", {
            node_id: node.id,
          });
          // Abilities dependem do nó selecionado — recarrega o perfil pra refletir o novo contexto
          this.$store.commit("profileUpdate");
        } catch {
          // falha silenciosa — o redirecionamento prossegue
        }
      }

      this.$router.push("/");
    },

    logout() {
      axios.post(this.$store.state.api + "logout").finally(() => {
        localStorage.clear();
        this.$store.commit("setNode", null);
        this.$router.push("/login");
      });
    },
  },

  mounted() {
    if (this.$store.state.user.id) {
      this.loadNodes();
    } else {
      const unwatch = this.$watch(
        () => this.$store.state.user.id,
        (id) => {
          if (id) {
            this.loadNodes();
            unwatch();
          }
        },
      );
      setTimeout(() => {
        if (this.loading) this.loadNodes();
      }, 1500);
    }
  },
};
</script>

<style lang="scss" scoped>
.node-selector-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f8f8;
  padding: 1.5rem;
}

.node-selector-inner {
  background: #fff;
  border-radius: 12px;
  padding: 2rem;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.nodes-tree-panel {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  max-height: 360px;
  overflow-y: auto;
  padding: 6px 4px;
}

.selected-info {
  font-size: 0.82rem;
  display: flex;
  align-items: center;
  padding: 4px 6px;
  background: #f5f3ff;
  border-radius: 6px;
  border: 1px solid #d4ceff;
}
</style>
